/**
 * @file api-site-designs.ts
 * @description Клиент библиотеки дизайнов сайта `admin/site-view/designs`
 * (phase19.16 Р1-6а, §7.9 фазы; контракт site-view-api-contract.md §14.2).
 *
 * Дизайн — именованный набор: оформление целиком, рисунок подвала и (по
 * галочке) логотипы. Применение пишет его в черновик вида сайта и ничего не
 * публикует. Все действия — право настроек магазина (`SHOP_MANAGE_SETTINGS`,
 * иначе 403 `site_design_forbidden`).
 *
 * «Создать новый» (решение 2026-10-12): `POST` с `from` (пустой шаблон или
 * копия дизайна) и `apply: true` — сразу в черновик в той же транзакции.
 * «Обновить из текущего вида» — `POST /:id/update-from-draft`.
 *
 * Ошибки: 409 `site_design_name_taken`, 422 `site_design_limit`
 * (`details: { used, max }`), 409 `site_design_in_use`, 409
 * `site_view_version_conflict`, 400 `site_design_brand_version_required`,
 * 400 `site_design_versions_invalid`, 400 `site_design_name_required`,
 * 404 `site_design_not_found`, 409 `site_design_assigned_to_pages`
 * (`details: { pages, count }`).
 */

import type { SiteDesignSummary, SiteTheme } from "@chaiberi/shared";

import { apiFetch } from "./api-client";
import type { SiteBrandDraft, SiteMedia, SitePartState } from "./site-view/types";

const BASE = "/admin/site-view/designs";

/** Страница, которой назначен дизайн. */
export interface SiteDesignPageRef {
  id: string;
  title: string;
}

export interface SiteDesignItem {
  id: string;
  name: string;
  /** `IMPORT` — «Как было», созданный при переходе на конструктор. */
  source: "SAVED" | "IMPORT";
  createdAt: string;
  /** Кто сохранил содержимое (при «Обновить» — обновивший); `null` — сотрудника больше нет. */
  createdByName: string | null;
  /** Последнее изменение: обновление содержимого или переименование. */
  updatedAt: string;
  /** Совпадает с опубликованным видом («На сайте»). */
  matchesPublished: boolean;
  /** Совпадает с черновиком («В работе»). */
  matchesDraft: boolean;
  canDelete: boolean;
  /** Почему удалить нельзя: `published` — стоит на сайте, `pages` — назначен страницам. */
  deleteBlockers: string[];
  /** Страниц с этим дизайном: > 0 — «Обновить из текущего вида» недоступно. */
  assignedPagesCount: number;
  /** Первые пять таких страниц по названию. */
  assignedPages: SiteDesignPageRef[];
  /** Краткое описание для карточки (без iframe): цвета ролей, названия шрифтов. */
  summary: SiteDesignSummary;
}

export interface SiteDesignList {
  items: SiteDesignItem[];
  limit: { used: number; max: number };
}

export interface SiteDesignDetail extends SiteDesignItem {
  theme: SiteTheme;
  brandVisual: { logoLight: SiteMedia | null; logoDark: SiteMedia | null; footerDecor: SiteMedia | null };
}

/** Из чего создать: текущий вид (умолчание), умолчание платформы или копия дизайна. */
export type SiteDesignFrom = { kind: "draft" } | { kind: "empty" } | { kind: "design"; designId: string };

/** Версии частей черновика, которые видел сотрудник. */
export interface SiteDesignExpectedVersions {
  THEME: number;
  BRAND?: number;
}

export interface CreateSiteDesignInput {
  name: string;
  /** Из чего создать; нет — текущий вид (как раньше). */
  from?: SiteDesignFrom;
  /** Только для текущего вида: черновик (по умолчанию) или опубликованный. */
  source?: "DRAFT" | "PUBLISHED";
  /** Только для текущего вида: «вместе с логотипами». */
  withLogos?: boolean;
  /** Сразу применить к черновику (нужны версии THEME и BRAND). */
  apply?: boolean;
  expectedVersions?: SiteDesignExpectedVersions;
}

/** Ответ «Применить»: новые версии частей, значения и файлы, которых больше нет. */
export interface ApplySiteDesignResult {
  parts: { THEME: SitePartState; BRAND: SitePartState };
  theme: SiteTheme;
  brand: SiteBrandDraft;
  dropped: Array<{ field: string; label: string }>;
}

/** Ответ создания: дизайн и итог применения (`null`, если не применяли). */
export interface CreatedSiteDesign extends SiteDesignDetail {
  applied: ApplySiteDesignResult | null;
}

/** Подробности 409 `site_design_assigned_to_pages`. */
export interface AssignedPagesDetails {
  pages: SiteDesignPageRef[];
  count: number;
}

export const siteDesigns = {
  list: () => apiFetch<SiteDesignList>(BASE),
  get: (id: string) => apiFetch<SiteDesignDetail>(`${BASE}/${id}`),
  create: (input: CreateSiteDesignInput) => apiFetch<CreatedSiteDesign>(BASE, { method: "POST", json: input }),
  rename: (id: string, name: string) => apiFetch<SiteDesignItem>(`${BASE}/${id}`, { method: "PATCH", json: { name } }),
  updateFromDraft: (id: string, withLogos: boolean) =>
    apiFetch<SiteDesignDetail>(`${BASE}/${id}/update-from-draft`, { method: "POST", json: { withLogos } }),
  remove: (id: string) => apiFetch<void>(`${BASE}/${id}`, { method: "DELETE" }),
  apply: (id: string, expectedVersions: SiteDesignExpectedVersions) =>
    apiFetch<ApplySiteDesignResult>(`${BASE}/${id}/apply`, { method: "POST", json: { expectedVersions } }),
};
